/** @jsxImportSource @emotion/react */
import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import ReactSelect from 'react-select'
import { toast } from 'react-toastify'

import { DmeOfficeOption, useDmeOfficeOptions } from 'hooks/useDmeOfficeOptions'
import { useGetDmeReferralPacketDefaultsQuery, useSendDmeReferralPacketMutation } from 'store/services/labOrder'
import { Order } from 'store/types'

// How the packet itself is marked among the order's files.
const DME_REFERRAL_SOURCE = 'DME_REFERRAL'

// What can go out by fax: a PDF or a photographed page.
const FAXABLE_FILE = /\.(pdf|png|jpe?g)$/i

const selectStyles = { menuPortal: (base: object) => ({ ...base, zIndex: 10000 }) }

type FileOption = {
  value: string
  label: string
}

interface IProps {
  order: Order
  onClose: () => void
}

// Sends the DME referral packet by hand (TES-3159): for a signed PAP Rx uploaded
// by hand, one that came in before the order was complete, or one with no DME on
// file. It is the same packet as the automatic send, listed in the Faxes panel and
// Outgoing Documents, noted on the chart and retried from there.
export const SendReferralPacketModal = ({ order, onClose }: IProps) => {
  const orderId = Number(order.id)
  const { data: defaults, isLoading: isLoadingDefaults } = useGetDmeReferralPacketDefaultsQuery({ orderId })
  const { options: dmeOptions, isLoading: isLoadingOffices } = useDmeOfficeOptions()
  const [sendPacket, { isLoading: isSending }] = useSendDmeReferralPacketMutation()
  const [signedRxFileKey, setSignedRxFileKey] = useState<string | null>(null)
  const [dmeOfficeId, setDmeOfficeId] = useState<number | null>(null)

  const fileOptions = useMemo<FileOption[]>(
    () =>
      Object.values(order.files || {})
        .filter((file) => file.source !== DME_REFERRAL_SOURCE && FAXABLE_FILE.test(file.filename || ''))
        .sort((first, second) => new Date(second.uploadDate).getTime() - new Date(first.uploadDate).getTime())
        .map((file) => ({
          value: file.key,
          label: `${file.filename} · ${file.uploadType} · ${moment(file.uploadDate).format('ll')}`,
        })),
    [order.files],
  )

  // Start from what the server suggests, without undoing a pick made meanwhile.
  useEffect(() => {
    if (!defaults) return

    setSignedRxFileKey((current) => current ?? defaults.signedRxFileKey)
    setDmeOfficeId((current) => current ?? defaults.recipient?.id ?? null)
  }, [defaults])

  const selectedFile = fileOptions.find((option) => option.value === signedRxFileKey) ?? null
  const selectedDme = dmeOptions.find((option) => option.value === dmeOfficeId) ?? null
  const isPreferredDme = !!defaults?.recipient && defaults.recipient.id === dmeOfficeId

  const handleSend = async () => {
    if (!signedRxFileKey || !dmeOfficeId) return

    try {
      const recipient = await sendPacket({ orderId, signedRxFileKey, dmeOfficeId }).unwrap()
      toast.success(`Referral packet queued to ${recipient.name}.`)
      onClose()
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to send the referral packet.')
    }
  }

  return (
    <Modal centered show onHide={onClose} size="lg">
      <Modal.Header>
        <Modal.Title>Send referral packet</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted">
          The DME is faxed a cover page with the patient&apos;s demographics and insurance, followed by the
          sleep study order, office notes, insurance cards, the sleep study report and the signed PAP Rx.
        </p>
        <div className="form-group">
          <label htmlFor="referralSignedRx">Signed PAP Rx</label>
          <ReactSelect
            inputId="referralSignedRx"
            classNamePrefix="select"
            isSearchable
            isLoading={isLoadingDefaults}
            placeholder="Choose the signed Rx..."
            value={selectedFile}
            onChange={(option) => setSignedRxFileKey((option as FileOption | null)?.value ?? null)}
            options={fileOptions}
            menuPortalTarget={document.body}
            styles={selectStyles}
          />
        </div>
        <div className="form-group">
          <label htmlFor="referralDme">DME</label>
          <ReactSelect
            inputId="referralDme"
            classNamePrefix="select"
            isSearchable
            isLoading={isLoadingDefaults || isLoadingOffices}
            placeholder="Search DME offices..."
            value={selectedDme}
            onChange={(option) => setDmeOfficeId((option as DmeOfficeOption | null)?.value ?? null)}
            options={dmeOptions}
            menuPortalTarget={document.body}
            styles={selectStyles}
          />
          {!isLoadingDefaults && (
            <small className="text-muted d-block mt-1" data-testid="referral-dme-hint">
              {isPreferredDme && 'The patient\'s or the office\'s preferred DME.'}
              {!defaults?.recipient && 'Neither the patient nor the office has a preferred DME.'}
            </small>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" disabled={!signedRxFileKey || !dmeOfficeId || isSending} onClick={handleSend}>
          {isSending ? 'Sending...' : 'Send packet'}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
